import { useSyncExternalStore } from 'react';

/**
 * Tab-scoped "acting as customer" context (see docs/ACTING-CONTEXT.md).
 *
 * The acting customer id lives in sessionStorage — which is PER-TAB (survives a
 * reload, is NOT shared across tabs) — so two tabs can be in two different
 * customers at once without stomping each other. The supabase client sends it
 * on every request as the `x-acting-customer` header; the database's
 * current_customer_id() honors it only after a membership/superadmin check, so
 * this is just a signal, never the security boundary.
 *
 * A fresh tab opened from the admin "Act as" / "Start order" buttons is seeded
 * via a `?act_as=<id>` query param, consumed once here at module load (before
 * the supabase client makes its first request) and stripped from the URL.
 */

const KEY = 'brix_acting_customer';
const EVENT = 'brix-acting-change';

// Seed from ?act_as= on first load, then strip it so it doesn't linger or get
// bookmarked. Runs at import time — before the supabase client is used.
(function seedFromUrl() {
  try {
    const u = new URL(window.location.href);
    const act = u.searchParams.get('act_as');
    if (act) {
      sessionStorage.setItem(KEY, act);
      u.searchParams.delete('act_as');
      window.history.replaceState({}, '', u.pathname + u.search + u.hash);
    }
  } catch {
    /* no window / storage (e.g. SSR) — ignore */
  }
})();

/** The customer id this tab is acting as, or null. */
export function getActingCustomer(): string | null {
  try {
    return sessionStorage.getItem(KEY) || null;
  } catch {
    return null;
  }
}

/** Set (or clear, with null) this tab's acting customer and notify subscribers. */
export function setActingCustomer(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(KEY, id);
    else sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
  try {
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* ignore */
  }
}

/**
 * Ensure every same-origin Netlify function call carries the per-tab acting
 * header (docs/ACTING-CONTEXT.md, step 3b) — without threading it through each
 * of the ~dozen customer-side hooks that call functions. Strictly scoped to
 * `/.netlify/functions/` URLs; all other requests (supabase.co, third parties)
 * pass through untouched, so nothing else can leak the header. Idempotent.
 *
 * (supabase-js calls already carry the header via the client's own fetch
 * wrapper in lib/supabase.ts; this covers the plain fetch() calls to functions.)
 */
let fetchPatched = false;
export function installFunctionActingHeader(): void {
  if (fetchPatched) return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  fetchPatched = true;
  const orig = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const acting = getActingCustomer();
      if (acting) {
        const url =
          typeof input === 'string'
            ? input
            : input instanceof URL
              ? input.href
              : (input as Request).url;
        if (url && url.includes('/.netlify/functions/')) {
          const headers = new Headers(
            init?.headers ?? (input instanceof Request ? input.headers : undefined),
          );
          headers.set('x-acting-customer', acting);
          return orig(input, { ...init, headers });
        }
      }
    } catch {
      /* any trouble → fall through to an untouched fetch */
    }
    return orig(input, init);
  };
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(EVENT, cb);
  return () => window.removeEventListener(EVENT, cb);
}

/** Reactive read of this tab's acting customer id (re-renders on change). */
export function useActingCustomerId(): string | null {
  return useSyncExternalStore(subscribe, getActingCustomer, () => null);
}
